import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Grid,
  Paper
} from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';

// I dati dei 3 step video
const stepsData = [
  {
    title: 'Come creare la tua prima attività didattica con Minera AI',
    videoUrl: '/video/Minerva_Tutorial_Parte_1.mp4',
  },
  {
    title: 'Come semplificare contenuti velocemente con Minerva',
    videoUrl: '/video/Minerva_Tutorial_personalizzazione_risorse.mp4',
  },
  {
    title: 'Come creare quiz interattivi e altri giochi su Minerva',
    videoUrl: '/video/Minerva_Tutorial_Creazione_Quiz.mp4',
  },
];

// Opzioni per il quiz a risposta multipla
const quizOptions = {
  tipoAttivita: [
    { value: 'scheda', label: 'Scheda didattica', icon: '' },
    { value: 'compito', label: 'Compito di realtà', icon: '' },
    { value: 'laboratorio', label: 'Laboratorio', icon: '' },
    { value: 'mappa', label: 'Mappa mentale', icon: '' },
    { value: 'attivita', label: 'Attività didattica innovativa', icon: '' },
    // { value: 'quiz', label: 'Crea quiz o verifiche interattive', icon: '', disabled: true }
  ],
  classi: [
    { value: 'Infanzia', label: 'Scuola dell\'infanzia', icon: '' },
    { value: 'Primaria', label: 'Scuola primaria', icon: '' },
    { value: 'Secondaria di primo grado', label: 'Scuola secondaria di I grado', icon: '' }
  ],
  materie: [
    { value: 'Sostegno', label: 'Sostegno', icon: '◉' },
    { value: 'Italiano', label: 'Italiano', icon: '◉' },
    { value: 'Storia', label: 'Storia', icon: '◉' },
    { value: 'Geografia', label: 'Geografia', icon: '◉' },
    { value: 'Matematica', label: 'Matematica', icon: '◉' },
    { value: 'Scienze', label: 'Scienze', icon: '◉' },
    { value: 'Tecnologia', label: 'Tecnologia', icon: '◉' },
    { value: 'Musica', label: 'Musica', icon: '◉' },
    { value: 'Arte e Immagine', label: 'Arte e immagine', icon: '◉' },
    { value: 'Inglese', label: 'Inglese', icon: '◉' },
    { value: 'Educazione Civica', label: 'Educazione civica', icon: '◉' },
    { value: 'Educazione Fisica', label: 'Educazione fisica', icon: '◉' },
    { value: 'Religione Cattolica', label: 'Religione cattolica', icon: '◉' },
    { value: 'Interdisciplinare', label: 'Interdisciplinare', icon: '◉' }
  ]
};

// Mappatura delle classi per ogni tipo di scuola
const classiPerScuola = {
  'Infanzia': ['Prima', 'Seconda', 'Terza'],
  'Primaria': ['Prima', 'Seconda', 'Terza', 'Quarta', 'Quinta'],
  'Secondaria di primo grado': ['Prima', 'Seconda', 'Terza']
};

/**
 * Wizard con 3 video e 1 step finale.
 * L'utente deve vedere almeno 1 minuto 
 * per non far comparire il "Sei sicuro di saltare?".
 *
 * @param {Function} onFinishWizard - callback quando si clicca "Ho capito!" nello step finale
 * @param {Object} profileData - dati del profilo dell'utente da useGetProfile
 */
const NoChatsWizard = ({ onFinishWizard, profileData }) => {
  // Stato per controllare se mostrare la schermata iniziale
  const [showChoiceScreen, setShowChoiceScreen] = useState(true);
  
  // Indice dello step corrente
  const [stepIndex, setStepIndex] = useState(0);
  // Se stepIndex === stepsData.length, siamo nello step finale di testo
  const isFinalStep = stepIndex === stepsData.length;

  // Stato che tiene traccia di quanti secondi sono stati visti del video corrente
  const [watchedTime, setWatchedTime] = useState(0);

  // Stato per il dialog "Sei sicuro di saltare?"
  const [openSkipDialog, setOpenSkipDialog] = useState(false);
  
  // Stato per il quiz a risposta multipla
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizStep, setQuizStep] = useState(0); // 0 = cosa vuoi creare
  const [quizAnswers, setQuizAnswers] = useState({
    tipoAttivita: ''
  });
  
  // Evidenziamo le materie del profilo - non serve più ma manteniamo per compatibilità
  const isSubjectInProfile = (subjectValue) => {
    if (!profileData || !profileData.subjects) return false;
    
    // Per ogni materia nel profilo, verifichiamo se c'è una corrispondenza
    return profileData.subjects.some(subject => {
      // Pulisci e normalizza le stringhe per il confronto
      const normalizedSubject = subject.trim().toLowerCase().replace(/\s+/g, ' ');
      const normalizedValue = subjectValue.trim().toLowerCase().replace(/\s+/g, ' ');
      
      // Confronto diretto
      if (normalizedSubject === normalizedValue) return true;
      
      // Confronto parziale (se una contiene l'altra)
      if (normalizedSubject.includes(normalizedValue) || normalizedValue.includes(normalizedSubject)) return true;
      
      // Confronti specifici per casi particolari
      if (normalizedSubject.includes('eduazione fisica') && normalizedValue.includes('educazione fisica')) return true;
      if (normalizedSubject.includes('arte') && normalizedValue.includes('arte')) return true;
      
      return false;
    });
  };

  // Utilizziamo i dati del profilo - non necessario ma mantenuto per compatibilità
  useEffect(() => {
    if (profileData) {
      // Mantenuto per compatibilità
      console.log('Profilo utente caricato:', profileData.firstName);
      
      // Logghiamo le materie del profilo per debug
      if (profileData.subjects && profileData.subjects.length > 0) {
        console.log('Materie dal profilo:', profileData.subjects);
      }
    }
  }, [profileData]);

  // Handler per chi sceglie "Cominciamo! Voglio creare qualcosa subito"
  const handleSkipTutorials = () => {
    // Invece di andare subito alla fine, mostriamo il quiz
    setShowChoiceScreen(false);
    setShowQuiz(true);
  };

  // Handler per chi sceglie "Preferisco prima guardare come funziona"
  const handleWatchTutorials = () => {
    setShowChoiceScreen(false);
  };
  
  // Handler per la selezione delle risposte nel quiz
  const handleQuizChange = (event) => {
    const { name, value } = event.target;
    setQuizAnswers(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handler per il pulsante "Avanti" nel quiz
  const handleQuizNext = () => {
    // Quiz completato, continua con il wizard originale
    setShowQuiz(false);
    // Passa le risposte al completamento del wizard
    onFinishWizard(quizAnswers);
  };
  
  // Handler per il pulsante "Indietro" nel quiz
  const handleQuizBack = () => {
    // Torna alla schermata di scelta
    setShowQuiz(false);
    setShowChoiceScreen(true);
  };

  // Listener per l'avanzamento del video
  const handleTimeUpdate = (event) => {
    const current = event.target.currentTime;
    setWatchedTime(current);
  };

  // Clic su "Visto"
  const handleNext = () => {
    // Se l'utente NON ha guardato 60 secondi => chiedi conferma
    if (watchedTime < 60) {
      setOpenSkipDialog(true);
    } else {
      // Altrimenti avanza direttamente
      goToNextStep();
    }
  };

  // Funzione che avanza di step e resetta il tempo di visione
  const goToNextStep = () => {
    if (stepIndex < stepsData.length - 1) {
      // Passa al prossimo video
      setStepIndex((prev) => prev + 1);
      setWatchedTime(0);
    } else {
      // Abbiamo finito i video, mostra lo step finale
      setStepIndex(stepsData.length); // Imposta l'indice per lo step finale
      // NON settiamo subito showQuiz a true, mostriamo prima lo step "Ora provaci tu!"
    }
  };

  // Clic su "Indietro"
  const handleBack = () => {
    setStepIndex((prev) => Math.max(0, prev - 1));
    setWatchedTime(0);
  };

  // Conferma skip dal Dialog
  const handleConfirmSkip = () => {
    setOpenSkipDialog(false);
    goToNextStep();
  };

  // Annulla skip -> rimani qui
  const handleCancelSkip = () => {
    setOpenSkipDialog(false);
  };

  // Torna alla schermata di scelta
  const handleBackToChoice = () => {
    setShowChoiceScreen(true);
  };

  // Stili per mostrare il secondo video più piccolo (è verticale ma senza rotazione)
  const getVideoStyles = (index) => {
    // Applica uno stile "ridotto" solo al secondo video (indice 1)
    if (index === 1) {
      return {
        display: 'block',
        margin: '0 auto',       // per centrarlo orizzontalmente
        width: 'auto',
        maxWidth: '300px',      // limite orizzontale (puoi modificare)
        maxHeight: '400px',     // limite MASSIMO di altezza per garantire visibilità pulsanti
        height: 'auto',
        objectFit: 'contain',
        borderRadius: '0.5rem',
        marginBottom: '1.5rem',
      };
    }
    // Per gli altri video (orizzontali) va bene il 100% classico
    return {
      width: '100%',
      height: 'auto',
      objectFit: 'contain',
      borderRadius: '0.5rem',
      marginBottom: '1.5rem',
    };
  };
  
  // Funzione per renderizzare il quiz a seconda dello step
  const renderQuiz = () => {
    // Ora c'è solo un tipo di opzioni: tipoAttivita
    const currentOptions = quizOptions.tipoAttivita;
    const currentField = 'tipoAttivita';
    
    const title = 'Cosa vorresti creare oggi?';
    const subtitle = 'Scegli il tipo di risorsa didattica che ti serve';

    return (
      <>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: '#333' }}>
          {title}
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, color: '#555' }}>
          {subtitle}
        </Typography>
        
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2}>
            {currentOptions.map((option) => {
              // Controlliamo se l'opzione è disabilitata
              const isDisabled = option.disabled === true;
              
              return (
                <Grid item xs={6} key={option.value}>
                  <Paper 
                    elevation={0}
                    sx={{
                      p: { xs: 1, sm: 2 }, // Riduco padding su mobile
                      borderRadius: '0.8rem',
                      border: '1px solid',
                      borderColor: quizAnswers[currentField] === option.value 
                        ? '#DF4634' 
                        : '#e0e0e0',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      bgcolor: quizAnswers[currentField] === option.value 
                        ? 'rgba(223,70,52,0.05)' 
                        : 'white',
                      transition: 'all 0.2s',
                      opacity: isDisabled ? 0.5 : 1,
                      pointerEvents: isDisabled ? 'none' : 'auto',
                      '&:hover': {
                        borderColor: isDisabled ? '#e0e0e0' : '#DF4634',
                        bgcolor: isDisabled ? 'white' : 'rgba(223,70,52,0.05)',
                      }
                    }}
                    onClick={() => {
                      if (!isDisabled) {
                        setQuizAnswers(prev => ({
                          ...prev,
                          [currentField]: option.value
                        }));
                      }
                    }}
                  >
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontSize: { xs: '0.8rem', sm: '1rem' },
                        textAlign: 'center',
                        width: '100%',
                        fontWeight: quizAnswers[currentField] === option.value ? 'bold' : 'normal',
                        color: quizAnswers[currentField] === option.value ? '#DF4634' : 'inherit'
                      }}
                    >
                      {option.label}
                    </Typography>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            onClick={handleQuizBack}
            sx={{
              borderRadius: '1.5rem',
              textTransform: 'none',
              borderColor: '#DF4634',
              color: '#DF4634',
              '&:hover': {
                backgroundColor: 'rgba(223,70,52,0.1)',
                borderColor: '#DF4634',
              },
            }}
          >
            Indietro
          </Button>
          
          <Button
            variant="contained"
            onClick={handleQuizNext}
            disabled={!quizAnswers[currentField]}
            sx={{
              backgroundColor: '#DF4634',
              textTransform: 'none',
              borderRadius: '1.5rem',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#E57373',
              },
              '&.Mui-disabled': {
                backgroundColor: '#f5f5f5',
                color: '#bdbdbd',
              },
            }}
          >
            Iniziamo!
          </Button>
        </Box>
      </>
    );
  };

  return (
    <>
      {/* Overlay scuro */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0,0,0,0.4)',
          zIndex: 1100,
          display: 'flex',
          justifyContent: { xs: 'center', sm: 'flex-start' },
          alignItems: 'center',
          paddingLeft: { xs: 0, sm: 'calc(60% - 400px)' },
        }}
      >
        <AnimatePresence mode="wait">
          {showChoiceScreen ? (
            <motion.div
              key="choice-screen"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              style={{
                backgroundColor: '#FFF',
                borderRadius: '1.875rem',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.2)',
                border: '1px solid #DF4634',
                width: '90%',
                maxWidth: '500px',
                padding: '2rem',
                textAlign: 'center',
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '1rem' }}>
                Ciao{profileData?.firstName ? ` ${profileData.firstName}` : ''}! Prima di iniziare…
              </Typography>
              
              <Typography variant="body1" sx={{ marginBottom: '2rem' }}>
                Vuoi metterti subito all'opera o guardare qualche consiglio utile per usare Minerva al meglio?
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {/* Opzione 1: Inizia subito */}
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    border: '1px solid #e0e0e0',
                    borderRadius: '0.75rem',
                    padding: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: '#DF4634',
                      boxShadow: '0 4px 12px rgba(223,70,52,0.15)',
                    }
                  }}
                  onClick={handleSkipTutorials}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#DF4634' }}>
                    Cominciamo! Voglio creare qualcosa subito.
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#757575' }}>
                    Ti guiderò passo passo, sarà facilissimo.
                  </Typography>
                </Box>
                
                {/* Opzione 2: Guarda i tutorial */}
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    border: '1px solid #e0e0e0',
                    borderRadius: '0.75rem',
                    padding: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: '#DF4634',
                      boxShadow: '0 4px 12px rgba(223,70,52,0.15)',
                    }
                  }}
                  onClick={handleWatchTutorials}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#DF4634' }}>
                    Preferisco prima guardare come funziona.
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#757575' }}>
                    Ho preparato 3 mini tutorial per te: chiari, brevi e pratici.
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          ) : showQuiz ? (
            <motion.div
              key={`quiz-step-${quizStep}`}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.4 }}
              style={{
                backgroundColor: '#FFF',
                borderRadius: '1.875rem',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.2)',
                border: '1px solid #DF4634',
                width: '90%',
                maxWidth: '700px',
                maxHeight: '85vh',
                overflowY: 'auto',
                padding: '2rem',
              }}
            >
              {renderQuiz()}
            </motion.div>
          ) : (
            <motion.div
              key={isFinalStep ? 'final-step' : `step-${stepIndex}`}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.4 }}
              style={{
                backgroundColor: '#FFF',
                borderRadius: '1.875rem',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.2)',
                border: '1px solid #DF4634',
                width: '90%',
                maxWidth: '600px',
                maxHeight: '85vh',
                overflowY: 'auto',
                padding: '1.5rem',
                textAlign: 'center',
                position: 'relative',
              }}
            >
              {/* Se NON step finale => video */}
              {!isFinalStep ? (
                <>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, marginBottom: '1rem' }}
                  >
                    {stepsData[stepIndex].title}
                  </Typography>

                  {/* PROGRESS BAR + Contatore step solo per i video */}
                  <Box sx={{ marginBottom: '1rem' }}>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, marginBottom: '0.25rem' }}
                    >
                      Video {stepIndex + 1} di {stepsData.length}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={((stepIndex + 1) / stepsData.length) * 100}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: '#FFD7D2',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: '#DF4634',
                        },
                      }}
                    />
                  </Box>

                  <Box sx={{ textAlign: 'right', mb: 1 }}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleBackToChoice}
                      sx={{
                        color: '#757575',
                        textTransform: 'none',
                        '&:hover': {
                          color: '#DF4634',
                        },
                      }}
                    >
                      Torna alla scelta iniziale
                    </Button>
                  </Box>
                  
                  {/* Mostro i pulsanti in alto quando siamo al secondo video */}
                  {stepIndex === 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={handleBack}
                        sx={{
                          borderRadius: '1.5rem',
                          textTransform: 'none',
                          borderColor: '#DF4634',
                          color: '#DF4634',
                          '&:hover': {
                            backgroundColor: 'rgba(223,70,52,0.1)',
                            borderColor: '#DF4634',
                          },
                        }}
                      >
                        Indietro
                      </Button>

                      <Button
                        variant="contained"
                        onClick={handleNext}
                        sx={{
                          backgroundColor: '#DF4634',
                          textTransform: 'none',
                          borderRadius: '1.5rem',
                          fontWeight: 'bold',
                          '&:hover': {
                            backgroundColor: '#E57373',
                          },
                        }}
                      >
                        Visto
                      </Button>
                    </Box>
                  )}

                  <video
                    src={stepsData[stepIndex].videoUrl}
                    controls
                    onTimeUpdate={handleTimeUpdate}
                    style={getVideoStyles(stepIndex)}
                  />

                  {/* Bottoni Indietro / Visto solo per i video che NON sono il secondo */}
                  {stepIndex !== 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      {stepIndex > 0 ? (
                        <Button
                          variant="outlined"
                          onClick={handleBack}
                          sx={{
                            borderRadius: '1.5rem',
                            textTransform: 'none',
                            borderColor: '#DF4634',
                            color: '#DF4634',
                            '&:hover': {
                              backgroundColor: 'rgba(223,70,52,0.1)',
                              borderColor: '#DF4634',
                            },
                          }}
                        >
                          Indietro
                        </Button>
                      ) : (
                        <Box />
                      )}

                      <Button
                        variant="contained"
                        onClick={handleNext}
                        sx={{
                          backgroundColor: '#DF4634',
                          textTransform: 'none',
                          borderRadius: '1.5rem',
                          fontWeight: 'bold',
                          '&:hover': {
                            backgroundColor: '#E57373',
                          },
                        }}
                      >
                        Visto
                      </Button>
                    </Box>
                  )}
                </>
              ) : (
                // Step finale => testo
                <>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, marginBottom: '1rem', fontSize: '1.2rem' }}
                  >
                    Ora provaci tu!
                  </Typography>
                  <Typography sx={{ fontSize: '0.95rem', marginBottom: '1.5rem' }}>
                    Sei pronta a creare la tua prima attività didattica con Minerva?
                    Ti accompagneremo passo dopo passo con un mini-tutorial pratico,
                    mostrandoti esattamente quali pulsanti cliccare e come impostare
                    la tua attività in pochi minuti. Iniziamo!
                  </Typography>

                  <Button
                    variant="contained"
                    onClick={() => setShowQuiz(true)}
                    sx={{
                      backgroundColor: '#DF4634',
                      textTransform: 'none',
                      borderRadius: '1.5rem',
                      paddingX: '2rem',
                      fontWeight: 'bold',
                      '&:hover': {
                        backgroundColor: '#E57373',
                      },
                    }}
                  >
                    Ho capito!
                  </Button>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      {/* Dialog "Sei sicuro di saltare?" */}
      <Dialog open={openSkipDialog} onClose={handleCancelSkip}>
        <DialogTitle>Saltare il tutorial?</DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di non voler terminare la visione di questo video tutorial?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSkip} sx={{ textTransform: 'none' }}>
            Annulla
          </Button>
          <Button
            onClick={handleConfirmSkip}
            variant="contained"
            sx={{
              backgroundColor: '#DF4634',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#E57373',
              },
            }}
          >
            Sì, salta
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NoChatsWizard;
